/**
 * Functional domains shown on Goals (4-up) and Tracking › Function (6-up).
 *
 * Sources differ by domain, because the schema only carries baselines for four
 * of them (`care_recipients.baseline_*`, 1 = best … 4 = most support):
 *   mobility, memory, mood, independence.
 * Hearing, vision and strength have no baseline column — they come from
 * `functional_assessments`, so they read "Not assessed yet" until a nurse
 * assessment has been run.
 */

export const BASELINE_LABELS = {
  1: 'Independent',
  2: 'Needs reminders',
  3: 'Needs help',
  4: 'Fully dependent',
}

/** 1 (best) … 4 (most support) → a 0–100 meter, higher = better. */
export function baselineToPct(v) {
  if (v == null) return null
  return Math.round(((5 - v) / 4) * 100)
}

export function statusFromPct(pct) {
  if (pct == null) return { label: 'Not assessed', tone: 'muted', arrow: '' }
  if (pct >= 70) return { label: 'Good', tone: 'good', arrow: '↑' }
  if (pct >= 45) return { label: 'Watch', tone: 'warn', arrow: '→' }
  return { label: 'Declining', tone: 'bad', arrow: '↓' }
}

export const TONE_COLOR = {
  good: 'var(--green)',
  warn: 'var(--amber)',
  bad: 'var(--danger)',
  muted: 'var(--muted)',
}

/* ── Reference ranges for the nurse mini-tests ──────────────────── */

export function tugStatus(seconds) {
  if (seconds == null) return null
  if (seconds < 12) return { text: `${seconds}s — within normal range`, pct: 85, tone: 'good' }
  if (seconds < 20) return { text: `${seconds}s — slower than typical`, pct: 55, tone: 'warn' }
  return { text: `${seconds}s — high fall risk`, pct: 25, tone: 'bad' }
}

export function recallStatus(score) {
  if (score == null) return null
  const pct = Math.round((score / 3) * 100)
  if (score === 3) return { text: 'Recalled all 3 words', pct, tone: 'good' }
  if (score === 2) return { text: 'Recalled 2 of 3 words', pct, tone: 'warn' }
  return { text: `Recalled ${score} of 3 words`, pct, tone: 'bad' }
}

export function chairStandStatus(count) {
  if (count == null) return null
  if (count >= 12) return { text: `${count} stands in 30s — normal`, pct: 85, tone: 'good' }
  if (count >= 8) return { text: `${count} stands in 30s — below average`, pct: 55, tone: 'warn' }
  return { text: `${count} stands in 30s — low`, pct: 25, tone: 'bad' }
}

export const HEARING_LABELS = {
  1: { text: 'Follows conversation clearly', pct: 90, tone: 'good' },
  2: { text: 'Asks for repeats', pct: 55, tone: 'warn' },
  3: { text: 'Significant difficulty', pct: 25, tone: 'bad' },
}

export const VISION_LABELS = {
  1: { text: 'No concerns reported', pct: 90, tone: 'good' },
  2: { text: 'Some difficulty', pct: 55, tone: 'warn' },
  3: { text: 'Significant difficulty', pct: 25, tone: 'bad' },
}

/** PHQ-2: 0–6. A total of 3+ warrants screening for depression. */
export function phq2Status(q1, q2) {
  if (q1 == null || q2 == null) return null
  const total = q1 + q2
  if (total >= 3) {
    return { text: `PHQ-2 score ${total} of 6 — screen for depression`, pct: 30, tone: 'bad', total }
  }
  return { text: `PHQ-2 score ${total} of 6 — low risk`, pct: 85, tone: 'good', total }
}

/**
 * Builds the display model for all six domains from the recipient's
 * baselines plus the most recent assessment (either may be absent).
 */
export function buildDomains(recipient, assessment) {
  const a = assessment || {}

  const mobility =
    tugStatus(a.mobility_tug_seconds != null ? Number(a.mobility_tug_seconds) : null) ||
    fromBaseline(recipient?.baseline_mobility)
  const memory =
    recallStatus(a.memory_recall_score) || fromBaseline(recipient?.baseline_memory)
  const mood =
    phq2Status(a.phq2_q1, a.phq2_q2) || fromBaseline(recipient?.baseline_mood)

  return [
    { key: 'mobility', emoji: '🚶', name: 'Mobility', ...orEmpty(mobility) },
    { key: 'memory', emoji: '🧠', name: 'Memory', ...orEmpty(memory) },
    { key: 'hearing', emoji: '👂', name: 'Hearing', ...orEmpty(HEARING_LABELS[a.hearing_score]) },
    { key: 'vision', emoji: '👁️', name: 'Vision', ...orEmpty(VISION_LABELS[a.vision_score]) },
    { key: 'strength', emoji: '💪', name: 'Strength', ...orEmpty(chairStandStatus(a.strength_chair_stands)) },
    { key: 'mood', emoji: '🙂', name: 'Mood', ...orEmpty(mood) },
  ]
}

function fromBaseline(v) {
  const pct = baselineToPct(v)
  if (pct == null) return null
  const s = statusFromPct(pct)
  return { text: BASELINE_LABELS[v] || s.label, pct, tone: s.tone }
}

function orEmpty(x) {
  return x || { text: 'Not assessed yet', pct: null, tone: 'muted' }
}
